/**
 * Product Addons - UI component for selecting addon products.
 * Supports variant-level addons with product-level fallback.
 * Injects properly named hidden inputs for FormData to work with items[] format.
 */

if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      constructor() {
        super();
        this.productFormId = this.dataset.productFormId;
        this.sectionId = this.dataset.sectionId;
        this.featuredQuantity = parseInt(this.dataset.featuredQuantity, 10) || 0;
        this.productForm = null;
        this.isExpanded = false;
        this.resizeObserver = null;
        this.accordionToggle = this.querySelector('[data-addons-toggle]');
        this._variantChangeUnsubscriber = null;
        this._changeHandler = this.handleChange.bind(this);
      }

      // Always reads from the currently visible set
      get activeSet() {
        return this.querySelector('[data-addons-set]:not([hidden])') || this.querySelector('[data-addons-set]');
      }

      get addonsList() {
        return this.activeSet ? this.activeSet.querySelector('[data-addons-list]') : null;
      }

      get addonItems() {
        return this.activeSet ? this.activeSet.querySelectorAll('.product-addons__item') : [];
      }

      get checkboxes() {
        return this.activeSet ? this.activeSet.querySelectorAll('.product-addons__input') : [];
      }

      connectedCallback() {
        this.productForm = document.getElementById(this.productFormId);

        // Delegate change events so they work for all sets without re-binding on switch
        this.addEventListener('change', this._changeHandler);

        // Use pubsub instead of variant:changed CustomEvent — the CustomEvent carries
        // this.currentVariant (old variant), while pubsub carries the new variant.
        // Filter by sectionId so quick-view or sticky-bar changes don't affect us.
        this._variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            this.handleVariantChanged(event.data.variant);
          }
        );

        this.setupCollapsible();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._changeHandler);
        if (this._variantChangeUnsubscriber) {
          this._variantChangeUnsubscriber();
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      handleChange(event) {
        if (event.target.matches('.product-addons__input')) {
          this.handleCheckboxChange(event);
        } else if (event.target.matches('[data-addon-variant-select]')) {
          this.handleVariantChange(event);
        }
      }

      /**
       * Switch the visible addon set when the product variant changes.
       * Shows the variant-specific set if one exists, otherwise the product-level fallback.
       */
      handleVariantChanged(variant) {
        const variantId = variant?.id?.toString();
        if (!variantId) return;

        const variantSet = this.querySelector(`[data-addons-set="${variantId}"]`);
        const fallbackSet = this.querySelector('[data-addons-set="fallback"]');
        const targetSet = variantSet || fallbackSet;

        // Deselect checked items in the current set before switching
        this.checkboxes.forEach((cb) => {
          if (cb.checked) {
            cb.checked = false;
            cb.closest('.product-addons__item')?.classList.remove('product-addons__item--selected');
          }
        });
        this.updateFormInputs();

        // Swap visibility
        this.querySelectorAll('[data-addons-set]').forEach((set) => {
          set.hidden = true;
        });
        if (targetSet) {
          targetSet.hidden = false;
        }

        // Re-setup collapsible for the new set
        this._teardownCollapsible();
        this.setupCollapsible();
      }

      _teardownCollapsible() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        this.isExpanded = false;
        this.classList.remove('product-addons--collapsible', 'product-addons--expanded');
        if (this.accordionToggle) {
          this.accordionToggle.hidden = true;
          this.accordionToggle.setAttribute('aria-expanded', 'false');
          // Remove old listener before re-adding in setupCollapsible
          this.accordionToggle.replaceWith(this.accordionToggle.cloneNode(true));
          this.accordionToggle = this.querySelector('[data-addons-toggle]');
        }
      }

      setupCollapsible() {
        const totalItems = this.addonItems.length;

        if (this.featuredQuantity <= 0 || totalItems <= this.featuredQuantity) {
          return;
        }

        if (this.accordionToggle) {
          this.accordionToggle.hidden = false;
          this.accordionToggle.addEventListener('click', this.handleAccordionToggle.bind(this));
        }

        this.classList.add('product-addons--collapsible');

        this.calculateHeights();

        this.resizeObserver = new ResizeObserver(() => {
          this.calculateHeights();
        });
        if (this.addonsList) {
          this.resizeObserver.observe(this.addonsList);
        }
      }

      calculateHeights() {
        if (!this.addonsList || this.featuredQuantity <= 0) return;

        const listStyles = getComputedStyle(this.addonsList);
        const gap = parseFloat(listStyles.gap) || 8;

        let featuredHeight = 0;
        this.addonItems.forEach((item, index) => {
          if (index < this.featuredQuantity) {
            featuredHeight += item.offsetHeight;
            if (index < this.featuredQuantity - 1) {
              featuredHeight += gap;
            }
          }
        });

        const peekHeight = 40;
        featuredHeight += gap + peekHeight;

        const fullHeight = this.addonsList.scrollHeight;

        this.addonsList.style.setProperty('--featured-height', `${featuredHeight}px`);
        this.addonsList.style.setProperty('--full-height', `${fullHeight}px`);
      }

      handleAccordionToggle() {
        this.isExpanded = !this.isExpanded;
        this.classList.toggle('product-addons--expanded', this.isExpanded);
        this.accordionToggle.setAttribute('aria-expanded', this.isExpanded.toString());
      }

      handleCheckboxChange(event) {
        const checkbox = event.target;
        const item = checkbox.closest('.product-addons__item');
        if (item) {
          item.classList.toggle('product-addons__item--selected', checkbox.checked);
        }
        this.updateFormInputs();
      }

      handleVariantChange(event) {
        const select = event.target;
        const item = select.closest('.product-addons__item');
        const selectedOption = select.options[select.selectedIndex];

        if (!item || !selectedOption) return;

        const checkbox = item.querySelector('.product-addons__input');
        const variantId = selectedOption.value;

        item.dataset.variantId = variantId;
        if (checkbox) {
          checkbox.dataset.variantId = variantId;
        }

        this.updatePriceDisplay(item, selectedOption);
        this.updateImage(item, selectedOption);
        this.updateAvailability(item, checkbox, selectedOption);

        if (checkbox?.checked) {
          this.updateFormInputs();
        }
      }

      hasSelectedAddons() {
        return Array.from(this.checkboxes).some((checkbox) => checkbox.checked);
      }

      getSelectedAddons() {
        const selectedAddons = [];

        this.checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            const item = checkbox.closest('.product-addons__item');
            const variantSelect = item.querySelector('[data-addon-variant-select]');

            const variantId = variantSelect ? variantSelect.value : checkbox.dataset.variantId;
            const quantityInput = item.querySelector('.quantity__input');
            const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

            selectedAddons.push({
              id: parseInt(variantId, 10),
              quantity: quantity,
            });
          }
        });

        return selectedAddons;
      }

      /**
       * Update form inputs to use items[] format when addons are selected.
       */
      updateFormInputs() {
        if (!this.productForm) return;

        const hasAddons = this.hasSelectedAddons();
        const mainIdInput = this.productForm.querySelector('input[name="id"], input[name="items[0][id]"]');
        const mainQtyInput = this.productForm.querySelector('input[name="quantity"], input[name="items[0][quantity]"]');

        this.productForm.querySelectorAll('.addon-input').forEach((el) => el.remove());

        if (hasAddons) {
          if (mainIdInput) mainIdInput.name = 'items[0][id]';
          if (mainQtyInput) mainQtyInput.name = 'items[0][quantity]';

          const mainVariantId = mainIdInput?.value;

          this.getSelectedAddons().forEach((addon, index) => {
            const itemIndex = index + 1;

            const addonIdInput = document.createElement('input');
            addonIdInput.type = 'hidden';
            addonIdInput.name = `items[${itemIndex}][id]`;
            addonIdInput.value = addon.id;
            addonIdInput.classList.add('addon-input');

            const addonQtyInput = document.createElement('input');
            addonQtyInput.type = 'hidden';
            addonQtyInput.name = `items[${itemIndex}][quantity]`;
            addonQtyInput.value = addon.quantity;
            addonQtyInput.classList.add('addon-input');

            const parentIdInput = document.createElement('input');
            parentIdInput.type = 'hidden';
            parentIdInput.name = `items[${itemIndex}][parent_id]`;
            parentIdInput.value = mainVariantId;
            parentIdInput.classList.add('addon-input');

            this.productForm.appendChild(addonIdInput);
            this.productForm.appendChild(addonQtyInput);
            this.productForm.appendChild(parentIdInput);
          });
        } else {
          if (mainIdInput && mainIdInput.name === 'items[0][id]') {
            mainIdInput.name = 'id';
          }
          if (mainQtyInput && mainQtyInput.name === 'items[0][quantity]') {
            mainQtyInput.name = 'quantity';
          }
        }
      }

      updatePriceDisplay(item, selectedOption) {
        const priceContainer = item.querySelector('[data-addon-price]');
        if (!priceContainer) return;

        const price = parseInt(selectedOption.dataset.price, 10);
        const comparePrice = parseInt(selectedOption.dataset.comparePrice, 10);
        const available = selectedOption.dataset.available === 'true';

        if (!available) {
          priceContainer.innerHTML = `<span class="product-addons__price--soldout">${FoxTheme.variantStrings?.soldOut || 'Sold out'}</span>`;
        } else if (comparePrice && comparePrice > price) {
          priceContainer.innerHTML = `
            <span class="product-addons__price--sale">${this.formatMoney(price)}</span>
            <span class="product-addons__price--compare"><s>${this.formatMoney(comparePrice)}</s></span>
          `;
        } else {
          priceContainer.innerHTML = `<span class="product-addons__price--regular">${this.formatMoney(price)}</span>`;
        }
      }

      updateImage(item, selectedOption) {
        const img = item.querySelector('.product-addons__img');
        if (!img || !selectedOption.dataset.image) return;

        const newImageUrl = selectedOption.dataset.image;
        img.src = newImageUrl;
        if (img.srcset) {
          const srcset = [80, 160]
            .map((w) => {
              const url = newImageUrl.replace(/width=\d+/, `width=${w}`);
              return `${url} ${w}w`;
            })
            .join(', ');
          img.srcset = srcset;
        }
      }

      updateAvailability(item, checkbox, selectedOption) {
        const available = selectedOption.dataset.available === 'true';
        const inputs = item.querySelectorAll('.quantity__button, .quantity__input');

        if (available) {
          item.classList.remove('product-addons__item--unavailable');
          if (checkbox) checkbox.disabled = false;
          inputs.forEach((el) => (el.disabled = false));
        } else {
          item.classList.add('product-addons__item--unavailable');
          if (checkbox) {
            checkbox.disabled = true;
            checkbox.checked = false;
          }
          item.classList.remove('product-addons__item--selected');
          inputs.forEach((el) => (el.disabled = true));
          this.updateFormInputs();
        }
      }

      formatMoney(cents) {
        if (typeof FoxTheme !== 'undefined' && FoxTheme.Currency?.formatMoney) {
          return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings?.moneyFormat);
        }
        return '$' + (cents / 100).toFixed(2);
      }
    }
  );
}

// Listen for quantity changes on addon items
document.addEventListener('change', (event) => {
  if (event.target.matches('.product-addons__item .quantity__input')) {
    const productAddons = event.target.closest('product-addons');
    if (productAddons) {
      productAddons.updateFormInputs();
    }
  }
});
